import { useState, useContext } from "react"
import { DateTime } from "luxon"
import { Formik, Form } from "formik"
import { Link } from "react-router-dom"
import _ from "lodash"
import { useQuery } from "@apollo/client"
import ROUTES from "../../../components/Routes/routesContants"
import { TRIM_TYPES, trimTextValue } from "../../../components/FieldTypes/helpers/trimTextValue"

// Context
import { UserContext } from "../../../context/userContext"

// Prime
import { InputText as Input } from "primereact/inputtext"
import { Dropdown } from "primereact/dropdown"
import { Password } from "primereact/password"
import { Calendar } from "primereact/calendar"

import { LoaderMedium } from "../../../components/Loaders"

// Static Files
import { LOAD_USER_FORM } from "../queries"
import styles from "../../../styles/Form.module.css"
import us_states from "../../../resources/us_states.json"

export default function FormProfile({ fields = {}, onSubmitHandler }) {
  const userCtx = useContext(UserContext)
  const [roleDefinition, setRoleDefintion] = useState(false)
  const [supervisors, setSupervisors] = useState(null)

  const { loading } = useQuery(LOAD_USER_FORM, {
    variables: {
      staff_id: userCtx.session.user.staff_id,
      organization_id: userCtx.session.user.organization_id,
    },
    onCompleted: (data) => {
      const roleOptions = _.map(data.roles, ({ rol_id, rol_name }) => {
        return { label: rol_name, value: rol_id }
      })

      const supervisorsOptions = _.map(
        data.supervisors,
        ({ staff_id, staff_name_first, staff_name_last }) => {
          return {
            label: `${staff_name_first} ${staff_name_last}`,
            value: staff_id,
          }
        }
      )

      setRoleDefintion(roleOptions)
      setSupervisors(supervisorsOptions)
    },
    fetchPolicy: "no-cache",
  })

  if (loading) return <LoaderMedium />

  const defaultFields = {
    staff_name_first: "",
    staff_name_last: "",
    staff_email_address: "",
    staff_password: "",
    staff_role: "",
    staff_primary_supervisor: "",
    staff_hire_date: "",
  }

  let staff_hire_date = ""
  if (!_.isEmpty(fields.staff_hire_date)) {
    staff_hire_date = DateTime.fromMillis(
      Number(fields.staff_hire_date)
    ).toJSDate()
  }

  const initialValues = {
    ...defaultFields,
    ...fields,
    staff_hire_date,
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={async (values) => {
        onSubmitHandler({
          variables: values,
        })
      }}
    >
      {({ values, isValid, setFieldValue }) => (
        <Form className={styles.form}>
          <div className={styles.blockWrapper}>
            <div className={styles.block}>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="staff_name_first">
                  First name:
                </label>
                <Input
                  id="staff_name_first"
                  name="staff_name_first"
                  value={values.staff_name_first}
                  onChange={({ target: { value } }) =>
                    setFieldValue("staff_name_first", trimTextValue(value))
                  }
                  className={styles.value}
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="staff_name_last">
                  Last name:
                </label>
                <Input
                  id="staff_name_last"
                  name="staff_name_last"
                  value={values.staff_name_last}
                  onChange={({ target: { value } }) =>
                    setFieldValue("staff_name_last", trimTextValue(value))
                  }
                  className={styles.value}
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="staff_email_address">
                  Primary account email address:
                </label>
                <div className={styles.description}>
                  A valid email address. All emails from the system will be sent
                  to this address. <br />
                  The email address is not made public and will only be used if
                  you wish to receive a new password or wish to receive certain
                  news or notifications by email.
                </div>
                <Input
                  id="staff_email_address"
                  name="staff_email_address"
                  value={values.staff_email_address}
                  onChange={({ target: { value } }) =>
                    setFieldValue("staff_email_address", trimTextValue(value, TRIM_TYPES.BOTH))
                  }
                  className={styles.value}
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="staff_password">
                  Password
                </label>
                <Password
                  value={values.staff_password}
                  onChange={(e) =>
                    setFieldValue("staff_password", e.target.value)
                  }
                  className={styles.value}
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="role_id">
                  Organization role
                </label>
                <Dropdown
                  value={values.staff_role}
                  options={roleDefinition}
                  onChange={(e) => setFieldValue("staff_role", e.value)}
                  placeholder="User role"
                  className={styles.value}
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="staff_country">
                  Country:
                </label>
                <Input
                  id="staff_country"
                  name="staff_country"
                  value={values.staff_country}
                  onChange={({ target: { value } }) =>
                    setFieldValue("staff_country", trimTextValue(value))
                  }
                  className={styles.value}
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="staff_state">
                  State:
                </label>
                <Dropdown
                  placeholder="Please select"
                  name="staff_state"
                  value={values.staff_state}
                  onChange={({ target: { value } }) =>
                    setFieldValue("staff_state", value)
                  }
                  options={us_states}
                  className={styles.value}
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="staff_city">
                  City:
                </label>
                <Input
                  id="staff_city"
                  name="staff_city"
                  value={values.staff_city}
                  onChange={({ target: { value } }) =>
                    setFieldValue("staff_city", trimTextValue(value))
                  }
                  className={styles.value}
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="staff_address">
                  Street address:
                </label>
                <Input
                  id="staff_address"
                  name="staff_address"
                  value={values.staff_address}
                  onChange={({ target: { value } }) =>
                    setFieldValue("staff_address", trimTextValue(value))
                  }
                  className={styles.value}
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="staff_zip_code">
                  Zip Code:
                </label>
                <Input
                  id="staff_zip_code"
                  name="staff_zip_code"
                  value={values.staff_zip_code}
                  onChange={({ target: { value } }) =>
                    setFieldValue("staff_zip_code", trimTextValue(value))
                  }
                  className={styles.value}
                />
              </div>
              <div className={styles.field}>
                <label
                  className={styles.label}
                  htmlFor="staff_phone_telerecovery"
                >
                  Assigned telerecovery number:
                </label>
                <Input
                  id="staff_phone_telerecovery"
                  name="staff_phone_telerecovery"
                  value={values.staff_phone_telerecovery}
                  onChange={({ target: { value } }) =>
                    setFieldValue("staff_phone_telerecovery", trimTextValue(value))
                  }
                  className={styles.value}
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="staff_credentials">
                  Staff member credentials:
                </label>
                <div className={styles.description}>
                  Staff credentials can be selected, if required for federal,
                  state, or other external reporting purposes.
                </div>
                <Input
                  id="staff_credentials"
                  name="staff_credentials"
                  value={values.staff_credentials}
                  onChange={({ target: { value } }) =>
                    setFieldValue("staff_credentials", trimTextValue(value))
                  }
                  className={styles.value}
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="staff_hire_date">
                  Date of hire:
                </label>
                <Calendar
                  name="staff_hire_date"
                  value={values.staff_hire_date}
                  onChange={({ target: { value } }) =>
                    setFieldValue("staff_hire_date", value)
                  }
                  maxDate={new Date()}
                  dateFormat="yy-mm-dd"
                  className={styles.value}
                />
              </div>
              <div className={styles.field}>
                <label
                  className={styles.label}
                  htmlFor="staff_primary_supervisor"
                >
                  Enter name and select supervisor
                </label>
                <Dropdown
                  value={values.staff_primary_supervisor}
                  options={supervisors}
                  onChange={(e) =>
                    setFieldValue("staff_primary_supervisor", e.value)
                  }
                  placeholder="Supervisor"
                  className={styles.value}
                />
              </div>
              <button
                type="submit"
                className="btn"
                disabled={!isValid}
              >
                Save
              </button>
              <Link to={ROUTES.USER_LIST} className={styles.btnBack}>
                Back
              </Link>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  )
}
